"use client";

import { useEffect, useState } from "react";
import Image from "next/image";
import Link from "next/link";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { heroImages } from "./heroSlides";

const headingClasses =
  "mt-2 max-w-[10.5rem] text-[1.02rem] font-black leading-[1.2] text-[#242424] md:mt-3 md:max-w-[16rem] md:text-[1.55rem] md:leading-[1.14] lg:max-w-[21rem] lg:text-[2.35rem]";

export default function HeroCarouselClient() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAutoPlay, setIsAutoPlay] = useState(true);
  const activeSlide = heroImages[currentSlide];

  useEffect(() => {
    if (!isAutoPlay) return;

    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % heroImages.length);
    }, 3400);

    return () => clearInterval(timer);
  }, [isAutoPlay]);

  const goToSlide = (index: number) => {
    setCurrentSlide(index);
    setIsAutoPlay(false);
  };

  const goToPrevious = () => {
    setCurrentSlide((prev) => (prev === 0 ? heroImages.length - 1 : prev - 1));
    setIsAutoPlay(false);
  };

  const goToNext = () => {
    setCurrentSlide((prev) => (prev + 1) % heroImages.length);
    setIsAutoPlay(false);
  };

  return (
    <div
      className="group absolute inset-0 h-full w-full overflow-hidden rounded-none md:rounded-[2rem]"
      onMouseEnter={() => setIsAutoPlay(false)}
      onMouseLeave={() => setIsAutoPlay(true)}
      role="region"
      aria-label="عرض الشرائح - YUMA"
    >
      <div className="relative h-full w-full">
        {heroImages.map((slide, index) => (
          <div
            key={slide.id}
            className={`absolute inset-0 transition-opacity duration-1000 ease-in-out ${
              index === currentSlide ? "opacity-100" : "opacity-0"
            }`}
            aria-hidden={index !== currentSlide}
          >
            <Image
              src={slide.image}
              alt={slide.alt}
              fill
              preload={index === 0}
              fetchPriority={index === 0 ? "high" : undefined}
              loading={index === 0 ? undefined : "lazy"}
              sizes="100vw"
              className="object-cover"
              quality={75}
            />
          </div>
        ))}
      </div>

      <div className="pointer-events-none absolute inset-0 z-10 flex items-end justify-start p-3 md:items-center md:p-10 lg:p-14">
        <div
          key={activeSlide.id}
          className="w-full max-w-[11.5rem] pb-14 text-right md:max-w-[28rem] md:pb-0"
        >
          <p className="text-[0.82rem] font-extrabold !text-[#ee376a] md:text-lg lg:text-[1.35rem]">
            {activeSlide.eyebrow}
          </p>
          {activeSlide.isPrimaryHeading ? (
            <h1 className={headingClasses}>{activeSlide.title}</h1>
          ) : (
            <h2 className={headingClasses}>{activeSlide.title}</h2>
          )}
          <p className="mt-1 max-w-[10.5rem] pb-1 text-[0.62rem] leading-[1.4] text-[#4f4f4f] md:mt-3 md:max-w-[19rem] md:pb-0 md:text-[0.92rem] md:leading-6 md:text-[#727272] lg:max-w-[21rem]">
            {activeSlide.description}
          </p>
        </div>
      </div>

      <div className="absolute bottom-4 right-3 z-20 flex items-center gap-2 md:bottom-5 md:right-10 md:gap-3 lg:bottom-12 lg:right-14">
        <Link
          href="/products"
          className="inline-flex min-h-8 items-center justify-center rounded bg-[var(--color-accent)] px-3 text-[0.8rem] font-extrabold text-white transition-colors hover:bg-[var(--color-orange)] md:min-h-12 md:rounded md:px-8 md:text-base md:font-bold"
        >
          {activeSlide.primaryCta}
        </Link>
        <Link
          href="/categories"
          className="inline-flex min-h-8 items-center justify-center gap-2 rounded border border-[rgba(241,89,34,0.18)] bg-white/96 px-3 text-[0.72rem] font-bold text-[var(--color-ink)] transition-colors hover:border-[var(--color-accent)] hover:text-[var(--color-accent)] md:min-h-12 md:rounded md:px-8 md:text-base"
        >
          {activeSlide.secondaryCta}
        </Link>
      </div>

      <div className="pointer-events-none absolute inset-x-4 top-1/2 z-10 flex -translate-y-1/2 items-center justify-between opacity-0 transition-opacity duration-300 group-hover:opacity-100 md:inset-x-6">
        <button
          onClick={goToPrevious}
          className="group pointer-events-auto relative z-10 flex h-10 w-10 items-center justify-center rounded-full border border-white/70 bg-white/90 transition-all duration-300 hover:border-[var(--color-accent)] md:h-12 md:w-12"
          aria-label="الشريحة السابقة"
          type="button"
        >
          <ChevronRight className="h-5 w-5 text-[var(--color-accent)] transition-transform group-hover:translate-x-0.5 md:h-6 md:w-6" />
        </button>

        <button
          onClick={goToNext}
          className="group pointer-events-auto relative z-10 flex h-10 w-10 items-center justify-center rounded-full border border-white/70 bg-white/90 transition-all duration-300 hover:border-[var(--color-accent)] md:h-12 md:w-12"
          aria-label="الشريحة التالية"
          type="button"
        >
          <ChevronLeft className="h-5 w-5 text-[var(--color-accent)] transition-transform group-hover:-translate-x-0.5 md:h-6 md:w-6" />
        </button>
      </div>

      <div className="absolute bottom-[-0.35rem] left-1/2 z-10 flex -translate-x-1/2 gap-1.5 md:bottom-[-0.3rem] md:gap-2 lg:bottom-6">
        {heroImages.map((_, index) => (
          <button
            key={index}
            onClick={() => goToSlide(index)}
            className="group flex h-8 w-8 items-center justify-center rounded-full transition-all duration-300 md:h-10 md:w-10"
            aria-label={`انتقل إلى الشريحة ${index + 1}`}
            aria-current={index === currentSlide ? "page" : undefined}
            type="button"
          >
            <span
              className={`block rounded-full transition-all duration-300 ${
                index === currentSlide
                  ? "h-1.5 w-6 bg-[var(--color-accent)] md:h-2 md:w-8"
                  : "h-1.5 w-1.5 bg-white/80 group-hover:bg-[var(--color-pink-light)] md:h-2 md:w-2"
              }`}
            />
          </button>
        ))}
      </div>
    </div>
  );
}
